import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { CircularProgress } from "@mui/material";

import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { TabsContent } from "@/features/widget-forms/forms/weather-model-map/components/tabs";
import { TimeSeriesConfig } from "@/components/graph/graph.types";
import TimeseriesTable from "@/features/dashboard/components/timeseries/timeseries-table";
import TimeseriesGraphs from "@/features/dashboard/components/timeseries/timeseries-graphs";
import ScrollArea from "@/components/scroll-area/scroll-area";
import { DimensionValue } from "@/lib/dimensions";
import Stack from "@/components/stack/stack";
import Spacing from "@/components/spacing/spacing";
import { useTranslation } from "@/lib/i18n";

export type MapParameterTimeSeriesResult = ParameterTimeSeriesChartResult;

export type ParameterTimeSeriesFetchRequest = {
  parameterId: string;
  unitCode: string;
};

interface MapControlTimeseriesContentProps {
  timeSeriesConfigs: TimeSeriesConfig[];
  currentTime: DimensionValue;
  fetchRequests: ParameterTimeSeriesFetchRequest[];
  queryKeyPrefix: readonly unknown[];
  fetchParameterSeries: (
    request: ParameterTimeSeriesFetchRequest,
  ) => Promise<MapParameterTimeSeriesResult | undefined>;
  pending?: boolean;
}

export function MapControlTimeseriesContent({
  timeSeriesConfigs,
  currentTime,
  fetchRequests,
  queryKeyPrefix,
  fetchParameterSeries,
  pending = false,
}: MapControlTimeseriesContentProps) {
  const { t } = useTranslation();
  const combinedQueries = useQueries({
    queries: fetchRequests.map(({ parameterId, unitCode }) => ({
      queryKey: [...queryKeyPrefix, parameterId, unitCode],
      queryFn: () => fetchParameterSeries({ parameterId, unitCode }),
      placeholderData: keepPreviousData,
      retry: false,
    })),
    combine: (queryResults) => {
      return {
        results: queryResults
          .map((result) => result.data)
          .filter((x): x is MapParameterTimeSeriesResult => !!x),
        pending: pending || queryResults.some((result) => result.isPending),
      };
    },
  });

  if (combinedQueries.pending) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <Spacing top={4}>
          <CircularProgress size={24} color="inherit" />
        </Spacing>
      </Stack>
    );
  }

  if (!combinedQueries.results.some((d) => (d.data?.datapoints.length ?? 0) >= 1)) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <Spacing top={2}>
          <span>{t("widgets.no_data")}</span>
        </Spacing>
      </Stack>
    );
  }

  return (
    <ScrollArea viewportProps={{ style: { maxHeight: "calc(100cqh - 194px)" } }}>
      <TabsContent value="graphs">
        <TimeseriesGraphs
          timeSeriesConfigs={timeSeriesConfigs}
          data={combinedQueries.results}
          currentTime={currentTime}
        />
      </TabsContent>
      <TabsContent value="table">
        <TimeseriesTable
          data={combinedQueries.results}
          timeSeriesConfigs={timeSeriesConfigs}
        />
      </TabsContent>
    </ScrollArea>
  );
}

function buildMapTimeseriesFetchRequests(
  timeSeriesConfigs: TimeSeriesConfig[],
): ParameterTimeSeriesFetchRequest[] {
  const seen = new Set<string>();
  const requests: ParameterTimeSeriesFetchRequest[] = [];

  for (const config of timeSeriesConfigs) {
    const unitCode = config.unit?.code;
    if (!unitCode) continue;
    for (const series of config.series) {
      const key = `${series.parameter_id}:${unitCode}`;
      if (seen.has(key)) continue;
      seen.add(key);
      requests.push({ parameterId: series.parameter_id, unitCode });
    }
  }

  return requests;
}

export function useMapTimeseriesFetchRequests(
  timeSeriesConfigs: TimeSeriesConfig[],
): ParameterTimeSeriesFetchRequest[] {
  return useMemo(
    () => buildMapTimeseriesFetchRequests(timeSeriesConfigs),
    [timeSeriesConfigs],
  );
}

export function useMapTimeseriesParameterIds(timeSeriesConfigs: TimeSeriesConfig[]) {
  return useMemo(
    () =>
      buildMapTimeseriesFetchRequests(timeSeriesConfigs).map(
        (request) => request.parameterId,
      ),
    [timeSeriesConfigs],
  );
}
